import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { TagGroupTag, TagGroupTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal } from '../helpers'

@Component({
  selector: 'flx-tag-group-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './tag-group-wrapper.component.html',
  styleUrl: './tag-group-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTagGroupWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<TagGroupTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  readonly isDynamicDataSource = computed(() => !!this.flowxProps()?.isDynamicDataSource)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  tagsSource: Signal<TagGroupTag[]>

  readonly tags = computed(() => {
    if (this.isDynamicDataSource()) {
      const resolved = this.tagsSource()
      return Array.isArray(resolved) ? resolved : []
    }
    return this.flowxProps()?.tags ?? []
  })

  readonly sizeClass = computed(() => `tag-group--${this.flowxProps()?.size || 'md'}`)
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.sizeClass()].filter(Boolean).join(' ')
  )

  readonly tagClass = (variant: string): string => `tag-group__tag--${variant || 'neutral'}`

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      // `getComputedValueSignal` evaluates the expression via `new Function` and preserves the
      // raw resolved value (array), unlike `getDynamicPropSignal` which stringifies via text
      // interpolation — required to keep `tags` as actual TagGroupTag objects.
      this.tagsSource = getComputedValueSignal(
        props.tagsSource || '[]',
        templateConfig
      ) as unknown as Signal<TagGroupTag[]>
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
